import { Link } from "react-router-dom";
import { ArrowLeft, Award, CheckCircle, Shield, Leaf, Lock, Users, Target, TrendingUp, ClipboardCheck, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useEffect } from "react";

const QualityStandards = () => {
  const { language } = useLanguage();
  const isArabic = language === "ar";

  useEffect(() => {
    document.title = isArabic
      ? "معايير الجودة - مؤسسة العربية الواحة"
      : "Quality Standards - Arabian Al Waha Est";
    window.scrollTo(0, 0);
  }, [isArabic]);

  const t = {
    back: isArabic ? "العودة إلى الموقع" : "Back to website",
    title: isArabic ? "معايير الجودة" : "Quality Standards",
    subtitle: isArabic ? "التميز في كل مشروع نقوم به" : "Excellence in every project we deliver",
    intro: isArabic
      ? "تلتزم مؤسسة العربية الواحة بأعلى معايير الجودة والسلامة والاستدامة في جميع خدماتها. نتبع أُطر عمل دولية معتمدة لضمان نتائج موثوقة ومتسقة لعملائنا."
      : "Arabian Al Waha Est is committed to the highest standards of quality, safety, and sustainability across all its services. We follow internationally recognised frameworks to deliver reliable and consistent results to our clients.",
    certificationsTitle: isArabic ? "شهاداتنا" : "Our Certifications",
    certifications: [
      { icon: CheckCircle, label: "ISO 9001", desc: isArabic ? "نظام إدارة الجودة" : "Quality Management System" },
      { icon: Leaf, label: "ISO 14001", desc: isArabic ? "إدارة البيئة" : "Environmental Management" },
      { icon: Shield, label: "ISO 45001", desc: isArabic ? "السلامة والصحة المهنية" : "Occupational Health & Safety" },
      { icon: Lock, label: "ISO 27001", desc: isArabic ? "أمن المعلومات" : "Information Security" },
    ],
    pillarsTitle: isArabic ? "ركائز الجودة" : "Quality Pillars",
    sections: [
      {
        icon: Target,
        title: isArabic ? "التركيز على العميل" : "Customer Focus",
        content: isArabic
          ? "نضع متطلبات العميل في صميم كل قرار. نستمع، ونوثق، ونقدم حلولاً مصممة خصيصاً تلبي توقعاتك وتتجاوزها."
          : "We place customer requirements at the heart of every decision. We listen, document, and deliver tailored solutions that meet and exceed expectations.",
      },
      {
        icon: ClipboardCheck,
        title: isArabic ? "ضبط الجودة" : "Quality Control",
        content: isArabic
          ? "نتبع إجراءات تفتيش ومراجعة موثقة في كل مرحلة من مراحل المشروع، من التخطيط وحتى التسليم النهائي، لضمان الالتزام بالمواصفات."
          : "We follow documented inspection and review procedures at every project stage — from planning through to final handover — to ensure full conformance with specifications.",
      },
      {
        icon: Shield,
        title: isArabic ? "السلامة أولاً" : "Safety First",
        content: isArabic
          ? "السلامة قيمة غير قابلة للتفاوض. نطبق أنظمة إدارة السلامة المهنية وفقاً لـ ISO 45001 ومتطلبات أرامكو والعملاء الصناعيين."
          : "Safety is a non-negotiable value. We operate occupational safety systems aligned with ISO 45001 and the requirements of Aramco and other industrial clients.",
      },
      {
        icon: Leaf,
        title: isArabic ? "المسؤولية البيئية" : "Environmental Responsibility",
        content: isArabic
          ? "نلتزم بممارسات صديقة للبيئة، وإدارة النفايات، وتقليل البصمة الكربونية لمشاريعنا وفقاً لمعيار ISO 14001."
          : "We are committed to environmentally responsible practices, waste management, and reducing the carbon footprint of our projects in line with ISO 14001.",
      },
      {
        icon: Users,
        title: isArabic ? "كفاءة فريق العمل" : "Workforce Competency",
        content: isArabic
          ? "يخضع موظفونا لبرامج تدريب وتأهيل مستمرة لضمان امتلاكهم للمهارات والشهادات المطلوبة لكل دور."
          : "Our personnel undergo continuous training and qualification programmes to ensure they hold the skills and certifications required for each role.",
      },
      {
        icon: TrendingUp,
        title: isArabic ? "التحسين المستمر" : "Continuous Improvement",
        content: isArabic
          ? "نقيس الأداء، ونراجع ملاحظات العملاء، ونحدث عملياتنا باستمرار لرفع مستوى الجودة عبر جميع أقسامنا."
          : "We measure performance, review client feedback, and continuously refine our processes to raise quality across every division.",
      },
      {
        icon: Award,
        title: isArabic ? "الامتثال للوائح" : "Regulatory Compliance",
        content: isArabic
          ? "نلتزم بجميع الأنظمة واللوائح المعمول بها في المملكة العربية السعودية، بما في ذلك متطلبات الغرفة التجارية ومعايير القطاعات الصناعية."
          : "We comply with all applicable Saudi Arabian regulations, including Chamber of Commerce requirements and industry-specific standards.",
      },
      {
        icon: Mail,
        title: isArabic ? "تواصل بشأن الجودة" : "Quality Inquiries",
        content: isArabic
          ? "لطلب وثائق الجودة، أو الشهادات، أو تقديم ملاحظات حول الخدمة، يرجى التواصل معنا على: sales@arabianalwaha.com"
          : "To request quality documentation, certifications, or share feedback about our service, please contact us at: sales@arabianalwaha.com",
      },
    ],
    footerNote: isArabic
      ? "الجودة ليست هدفاً بل هي طريقة عملنا اليومية."
      : "Quality is not a goal — it is the way we work every day.",
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isArabic ? "rtl" : "ltr"}>
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/40">
        <div className="container mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-primary/80">
        <div className="container mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <Award className="w-12 h-12 text-secondary mx-auto mb-6" />
            <h1 className="text-white font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
              {t.title}
            </h1>
            <p className="text-white/70 text-sm">{t.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-6 lg:px-10 max-w-3xl">
          <p className="text-lg text-muted-foreground leading-relaxed text-center">{t.intro}</p>
        </div>
      </section>

      {/* Certifications */}
      <section className="pb-8">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <h2 className="text-2xl font-bold text-accent text-center mb-8">{t.certificationsTitle}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {t.certifications.map((c, i) => (
              <div key={i} className="bg-card rounded-xl p-5 shadow-soft border border-border/40 text-center hover:shadow-elegant transition-elegant">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg gradient-primary flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-white" />
                </div>
                <div className="font-bold text-accent">{c.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-12 pb-20 lg:pb-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <h2 className="text-2xl font-bold text-accent text-center mb-8">{t.pillarsTitle}</h2>
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <div key={index} className="bg-card rounded-xl p-6 lg:p-8 shadow-soft border border-border/40 hover:shadow-elegant transition-elegant">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center shrink-0">
                    <section.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-accent mb-3">{section.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground italic">{t.footerNote}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QualityStandards;